import React from 'react';
import { Delete, Info, MoreVertical, Shuffle, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ContestOverflowMenuProps {
  /** Contest id — used to keep test ids unique per card. */
  id: string;
  onQuickPick: () => void;
  onFavouritePick: () => void;
  onClear: () => void;
  onInfoClick: () => void;
  /**
   * Whether the user has made any picks. Clear Selection is hidden when false,
   * matching the old StickyActionBar behaviour (nothing to clear).
   */
  hasAnyPicks?: boolean;
  /**
   * When absent/false the Popular Pick item is hidden. Driven by
   * `style_config.showPopularPicks`, same gate the old action bar used.
   */
  showFavouritePick?: boolean;
  /**
   * Hides the pick-editing actions (Quick Pick / Popular Pick / Clear), leaving
   * only Contest Details. Used in view-only and results modes, where the old
   * action bar was not rendered at all.
   */
  hidePickActions?: boolean;
}

/**
 * ContestOverflowMenu — three-dot overflow menu replacing the contest info icon
 * (DKPLAT-664).
 *
 * Rendered in the header banner when that banner is enabled, and in the contest
 * card header when it is not — whichever surface owns the info icon. Folds the
 * Quick Pick / Popular Pick / Clear actions in, so the separate action row can
 * be dropped and more of the questions list fits on screen. Gated behind
 * `style_config.header.showOverflowMenu`; when that flag is off the info icon +
 * action bar render as before.
 *
 * Built in one place (CompetitionView) and passed to whichever surface hosts it,
 * so the two placements cannot drift apart.
 */
const ContestOverflowMenu: React.FC<ContestOverflowMenuProps> = ({
  id,
  onQuickPick,
  onFavouritePick,
  onClear,
  onInfoClick,
  hasAnyPicks = false,
  showFavouritePick = false,
  hidePickActions = false,
}) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex-shrink-0 p-1 hover:opacity-80 transition-opacity text-text-primary"
          aria-label={t('actions.moreOptions')}
          data-test-id={`contest-overflow-menu-${id}`}
        >
          <MoreVertical className="w-7 h-7" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        {!hidePickActions && (
          <>
            <DropdownMenuItem onClick={onQuickPick} data-test-id="overflow-quick-pick">
              <Shuffle className="w-4 h-4 mr-2" />
              {t('actions.quickPick')}
            </DropdownMenuItem>

            {showFavouritePick && (
              <DropdownMenuItem onClick={onFavouritePick} data-test-id="overflow-favourite-pick">
                <Star className="w-4 h-4 mr-2" fill="currentColor" />
                {t('actions.favouritePick')}
              </DropdownMenuItem>
            )}

            {hasAnyPicks && (
              <DropdownMenuItem onClick={onClear} data-test-id="overflow-clear">
                <Delete className="w-4 h-4 mr-2" />
                {t('actions.clearSelection')}
              </DropdownMenuItem>
            )}
          </>
        )}

        <DropdownMenuItem onClick={onInfoClick} data-test-id="overflow-contest-details">
          <Info className="w-4 h-4 mr-2" />
          {t('actions.contestDetails')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ContestOverflowMenu;
